import React, { useEffect, useState } from "react";
import Template from "./Template";
import { useActions } from "../redux/actions";
import * as feeSchedulesActions from "../redux/actions/feeSchedules";
import * as practiceActions from "../redux/actions/practice";
import { Button, Checkbox, Dropdown, Form, Input, Label, Message, Table } from "semantic-ui-react";

const FEE_SCHED_TYPES = [
    { key: "Normal", text: "Normal", value: "Normal" },
    { key: "CoPay", text: "CoPay", value: "CoPay" },
    { key: "OutNetwork", text: "OutNetwork", value: "OutNetwork" },
    { key: "FixedBenefit", text: "FixedBenefit", value: "FixedBenefit" },
    { key: "ManualBlueBook", text: "ManualBlueBook", value: "ManualBlueBook" },
];

function OdFeeSchedules() {
    const FeeSchedulesActions = useActions(feeSchedulesActions);
    const PracticeActions = useActions(practiceActions);

    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [selectedPracticeIds, setSelectedPracticeIds] = useState<number[]>([]);
    const [includeHidden, setIncludeHidden] = useState(false);
    const [feeScheds, setFeeScheds] = useState<any[]>([]);
    const [searchDone, setSearchDone] = useState(false);
    const [filterPractice, setFilterPractice] = useState<string>("all");
    const [filterDescription, setFilterDescription] = useState<string>("");

    const createInit = {
        Description: "",
        FeeSchedType: "Normal",
        IsHidden: false,
        IsGlobal: true,
    };
    const [createForm, setCreateForm] = useState(createInit);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [operationResults, setOperationResults] = useState<{ practice: string; success: boolean; error?: string }[] | null>(null);

    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<any>({});

    const ALL_PRACTICES = -1;

    useEffect(() => {
        PracticeActions.getAllPractices().then((res: any) => {
            const opts = [
                { key: ALL_PRACTICES, text: "All Practices", value: ALL_PRACTICES },
                ...res.map((item: any) => ({
                    key: item.practiceId,
                    text: item.practice.practice,
                    value: item.practiceId,
                })),
            ];
            setPracticeOpts(opts);
        });
    }, []);

    const handlePracticeChange = (value: number[]) => {
        const justAddedAll = value.includes(ALL_PRACTICES) && !selectedPracticeIds.includes(ALL_PRACTICES);
        if (justAddedAll) {
            setSelectedPracticeIds([ALL_PRACTICES]);
        } else {
            setSelectedPracticeIds(value.filter((v) => v !== ALL_PRACTICES));
        }
    };

    const resolvePracticeIds = () => {
        const realIds = practiceOpts.filter((o: any) => o.value !== ALL_PRACTICES).map((o: any) => o.value);
        if (!selectedPracticeIds.length || selectedPracticeIds.includes(ALL_PRACTICES)) return realIds;
        return selectedPracticeIds;
    };

    const flattenResults = (res: any[]) => {
        const rows: any[] = [];
        res.forEach((entry: any) => {
            if (entry.success && Array.isArray(entry.data)) {
                entry.data.forEach((fs: any) => {
                    rows.push({ ...fs, _practice: entry.practice, _practiceId: entry.practiceId });
                });
            }
        });
        return rows;
    };

    const handleLoad = () => {
        const ids = resolvePracticeIds();
        const params: any = { includeHidden };

        FeeSchedulesActions.getOdFeeSchedules(ids, params).then((res: any) => {
            setFeeScheds(Array.isArray(res) ? flattenResults(res) : []);
            setFilterPractice("all");
            setFilterDescription("");
            setEditingKey(null);
            setSearchDone(true);
        });
    };

    const getVisibleRows = () => feeScheds.filter((f: any) =>
        (filterPractice === "all" || f._practice === filterPractice) &&
        (!filterDescription.trim() || (f.Description || "").toLowerCase().includes(filterDescription.toLowerCase()))
    );

    const isTruthy = (v: any) => String(v) === "true" || v === 1 || v === true;

    const handleCreate = () => {
        if (!createForm.Description) {
            alert("Description is required.");
            return;
        }
        const ids = resolvePracticeIds();
        const payload: any = {
            practiceIds: ids,
            Description: createForm.Description,
            FeeSchedType: createForm.FeeSchedType,
            IsHidden: createForm.IsHidden,
            IsGlobal: createForm.IsGlobal,
        };

        FeeSchedulesActions.createOdFeeSchedule(payload).then((res: any) => {
            if (Array.isArray(res)) {
                setOperationResults(res.map((r: any) => ({
                    practice: r.practice,
                    success: !!r.success,
                    error: r.error,
                })));
                setCreateForm(createInit);
                setShowCreateForm(false);
                handleLoad();
            }
        });
    };

    const rowKey = (fs: any, idx: number) => `${fs._practice}-${fs.FeeSchedNum}-${idx}`;

    const startEdit = (fs: any, idx: number) => {
        setEditingKey(rowKey(fs, idx));
        setEditForm({
            Description: fs.Description || "",
            IsHidden: isTruthy(fs.IsHidden),
            IsGlobal: isTruthy(fs.IsGlobal),
        });
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditForm({});
    };

    const saveEdit = (fs: any) => {
        const payload: any = {
            practiceId: fs._practiceId,
            FeeSchedNum: fs.FeeSchedNum,
            Description: editForm.Description,
            IsHidden: editForm.IsHidden,
            IsGlobal: editForm.IsGlobal,
        };

        FeeSchedulesActions.updateOdFeeSchedule(payload).then((res: any) => {
            if (res && res.success) {
                setFeeScheds((prev) => prev.map((row) =>
                    (row._practiceId === fs._practiceId && row.FeeSchedNum === fs.FeeSchedNum)
                        ? { ...row, Description: editForm.Description, IsHidden: editForm.IsHidden, IsGlobal: editForm.IsGlobal }
                        : row
                ));
                setOperationResults([{ practice: fs._practice, success: true }]);
                cancelEdit();
            } else {
                setOperationResults([{ practice: fs._practice, success: false, error: (res && res.error) || "Update failed" }]);
            }
        });
    };

    return (
        <Template activeLink="fee-schedules">
            <div className="ui card">
                <div className="content">
                    <h2>Fee Schedules</h2>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Practices</label>
                                <Dropdown
                                    placeholder="All Practices"
                                    options={practiceOpts}
                                    selection={true}
                                    search={true}
                                    multiple={true}
                                    value={selectedPracticeIds}
                                    onChange={(_e, { value }) => handlePracticeChange(value as number[])}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                                <Checkbox
                                    label="Include Hidden"
                                    checked={includeHidden}
                                    onChange={(_e, { checked }) => setIncludeHidden(!!checked)}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                <Button primary onClick={handleLoad}>Search</Button>
                                <Button onClick={() => setShowCreateForm(!showCreateForm)}>
                                    {showCreateForm ? "Cancel" : "Create Fee Schedule"}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>
            </div>

            {showCreateForm && (
                <div className="ui card">
                    <div className="content">
                        <h3>Create Fee Schedule</h3>
                        <p className="text-muted">Will be created in {selectedPracticeIds.length && !selectedPracticeIds.includes(ALL_PRACTICES) ? `${selectedPracticeIds.length} selected practice(s)` : 'all practices'}.</p>
                        <Form>
                            <Form.Group widths="equal">
                                <Form.Field required>
                                    <label>Description</label>
                                    <Input
                                        value={createForm.Description}
                                        onChange={(e) => setCreateForm({ ...createForm, Description: e.target.value })}
                                    />
                                </Form.Field>
                                <Form.Field required>
                                    <label>FeeSchedType</label>
                                    <Dropdown
                                        selection={true}
                                        options={FEE_SCHED_TYPES}
                                        value={createForm.FeeSchedType}
                                        onChange={(_e, { value }) => setCreateForm({ ...createForm, FeeSchedType: value as string })}
                                    />
                                </Form.Field>
                                <Form.Field style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
                                    <Checkbox
                                        label="IsHidden"
                                        checked={createForm.IsHidden}
                                        onChange={(_e, { checked }) => setCreateForm({ ...createForm, IsHidden: !!checked })}
                                    />
                                    <Checkbox
                                        label="IsGlobal"
                                        checked={createForm.IsGlobal}
                                        onChange={(_e, { checked }) => setCreateForm({ ...createForm, IsGlobal: !!checked })}
                                    />
                                </Form.Field>
                            </Form.Group>
                            <Button primary onClick={handleCreate}>Submit</Button>
                        </Form>
                    </div>
                </div>
            )}

            {operationResults && (
                <Message
                    onDismiss={() => setOperationResults(null)}
                    positive={operationResults.every(r => r.success)}
                    negative={operationResults.some(r => !r.success)}
                >
                    <Message.Header>
                        {operationResults.filter(r => r.success).length} succeeded, {operationResults.filter(r => !r.success).length} failed
                    </Message.Header>
                    <Message.List>
                        {operationResults.map((r, i) => (
                            <Message.Item key={i}>
                                <strong>{r.practice}</strong>: {r.success ? 'Success' : r.error || 'Failed'}
                            </Message.Item>
                        ))}
                    </Message.List>
                </Message>
            )}

            {searchDone && (
                <div className="ui card">
                    <div className="content">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}>Results ({getVisibleRows().length})</h3>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Input
                                    placeholder="Filter by Description..."
                                    value={filterDescription}
                                    onChange={(e) => setFilterDescription(e.target.value)}
                                    size="small"
                                />
                                <Dropdown
                                    placeholder="Filter by Practice"
                                    selection={true}
                                    clearable={true}
                                    options={Array.from(new Set(feeScheds.map((f: any) => f._practice))).map((p: any) => ({ key: p, text: p, value: p }))}
                                    value={filterPractice === "all" ? "" : filterPractice}
                                    onChange={(_e, { value }) => setFilterPractice(value ? value as string : "all")}
                                />
                            </div>
                        </div>
                        <Table celled={true}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Practice</Table.HeaderCell>
                                    <Table.HeaderCell>FeeSchedNum</Table.HeaderCell>
                                    <Table.HeaderCell style={{ width: '30%' }}>Description</Table.HeaderCell>
                                    <Table.HeaderCell>FeeSchedType</Table.HeaderCell>
                                    <Table.HeaderCell>IsHidden</Table.HeaderCell>
                                    <Table.HeaderCell>IsGlobal</Table.HeaderCell>
                                    <Table.HeaderCell>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {getVisibleRows().map((fs: any, idx: number) => {
                                    const key = rowKey(fs, idx);
                                    const isEditing = editingKey === key;
                                    return (
                                        <Table.Row key={key}>
                                            <Table.Cell><Label>{fs._practice}</Label></Table.Cell>
                                            <Table.Cell>{fs.FeeSchedNum}</Table.Cell>
                                            <Table.Cell>
                                                {isEditing ? (
                                                    <Input
                                                        fluid={true}
                                                        value={editForm.Description}
                                                        onChange={(e) => setEditForm({ ...editForm, Description: e.target.value })}
                                                    />
                                                ) : fs.Description}
                                            </Table.Cell>
                                            <Table.Cell>{fs.FeeSchedType}</Table.Cell>
                                            <Table.Cell>
                                                {isEditing ? (
                                                    <Checkbox
                                                        checked={editForm.IsHidden}
                                                        onChange={(_e, { checked }) => setEditForm({ ...editForm, IsHidden: !!checked })}
                                                    />
                                                ) : (isTruthy(fs.IsHidden) ? "Yes" : "No")}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {isEditing ? (
                                                    <Checkbox
                                                        checked={editForm.IsGlobal}
                                                        onChange={(_e, { checked }) => setEditForm({ ...editForm, IsGlobal: !!checked })}
                                                    />
                                                ) : (isTruthy(fs.IsGlobal) ? "Yes" : "No")}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {isEditing ? (
                                                    <Button.Group size="tiny">
                                                        <Button positive onClick={() => saveEdit(fs)}>Save</Button>
                                                        <Button onClick={cancelEdit}>Cancel</Button>
                                                    </Button.Group>
                                                ) : (
                                                    <Button size="tiny" icon="edit" content="Edit" onClick={() => startEdit(fs, idx)} />
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </Template>
    );
}

export default OdFeeSchedules;
